'use client';
import { useEffect, useRef, useSyncExternalStore } from 'react';
import { z } from 'zod';
import { clearVoiceSocketHandlers, consumeVoiceTicket, createVoiceSocket } from './socket';
import { elowenClient, ElowenApiError } from '../../lib/elowenClient';
import { VOICE_AUDIO_FRAME_MAX_BYTES, VOICE_CONTROL_FRAME_MAX_BYTES, VOICE_CALL_RECORD_BOUNDS, VOICE_CALL_STOP_BUDGET } from '../../lib/voice';
import type { VoiceBrowserControl, VoiceCallRequest, VoiceCallResponse, VoiceCallState, VoiceCallEndReason, VoiceTranscriptEntry } from '../../lib/types';
import { createVoiceAudio, type VoiceAudio } from 'elowen-plugin-shared/voiceAudio';

const refusal = z.enum(['spend_limit', 'not_granted', 'unknown_session', 'call_active', 'callback_unavailable', 'question_not_pending', 'disabled', 'daemon_restart', 'voice_unavailable', 'provider_unavailable', 'provider_error']);
const entry = z.object({ role: z.enum(['user', 'assistant']), text: z.string().max(VOICE_CALL_RECORD_BOUNDS.maxEntryChars) });
const control = z.union([
  z.object({ type: z.literal('transcript'), role: entry.shape.role, delta: z.string().max(VOICE_CALL_RECORD_BOUNDS.maxEntryChars), final: z.never().optional() }),
  z.object({ type: z.literal('transcript'), role: entry.shape.role, final: z.string().max(VOICE_CALL_RECORD_BOUNDS.maxEntryChars), delta: z.never().optional() }),
  z.object({ type: z.literal('submitted') }),
  z.object({ type: z.literal('audio_started'), itemId: z.string().min(1).max(1024) }),
  z.object({ type: z.literal('state'), state: z.enum(['connecting', 'listening', 'on_hold', 'ending']) }),
  z.object({ type: z.literal('ended'), reason: z.enum(['hangup', 'completed', 'max_duration', 'spend_limit', 'disconnected', 'silence', 'provider_error', 'daemon_restart']),
    submitted: z.boolean(), transcript: z.array(entry).max(VOICE_CALL_RECORD_BOUNDS.maxEntries)
      .refine(entries => entries.reduce((total, entry) => total + entry.text.length, 0) <= VOICE_CALL_RECORD_BOUNDS.maxTranscriptChars) }),
]);
interface CallDeps {
  audio(capture: (frame: ArrayBuffer) => void, played: (itemId: string, ms: number, speaking: boolean) => void, failed: () => void, signal: AbortSignal): Promise<VoiceAudio>;
  start(request: VoiceCallRequest): Promise<VoiceCallResponse>;
  /** Whether the browser already grants the microphone, so a paid call can start before capture opens. */
  microphoneGranted(): Promise<boolean>;
  socket(callId: string, ticket: string): WebSocket;
}
export interface VoiceCallSnapshot {
  phase: 'idle' | 'active' | 'ended' | 'error';
  state: VoiceCallState; speaking: boolean; transcript: VoiceTranscriptEntry[];
  reason?: VoiceCallEndReason | z.infer<typeof refusal> | 'mic_denied'; startedAt: number | null;
}
// Final transcripts are larger than browser control messages. Bound escaped JSON before parsing.
const DAEMON_FRAME_MAX_BYTES = VOICE_CALL_RECORD_BOUNDS.maxTranscriptChars * 6
  + VOICE_CALL_RECORD_BOUNDS.maxEntries * 64 + VOICE_CONTROL_FRAME_MAX_BYTES;
const idle = (): VoiceCallSnapshot => ({ phase: 'idle', state: 'connecting', speaking: false, transcript: [], startedAt: null });

export class VoiceCallSession {
  private value = idle();
  private listeners = new Set<() => void>();
  private audio?: VoiceAudio;
  private preparation?: AbortController;
  private socket?: WebSocket;
  private generation = 0;
  private hangingUp = false;
  private pending: Partial<Record<VoiceTranscriptEntry['role'], string>> = {};
  private timeout?: ReturnType<typeof setTimeout>;
  constructor(private deps: CallDeps) {}
  getSnapshot = (): VoiceCallSnapshot => this.value;
  subscribe = (listener: () => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };
  private update(patch: Partial<VoiceCallSnapshot>) {
    this.value = { ...this.value, ...patch };
    for (const listener of this.listeners) listener();
  }
  private send(message: VoiceBrowserControl) {
    if (this.socket?.readyState === 1) this.socket.send(JSON.stringify(message));
  }
  async start(target: string | ((signal: AbortSignal) => Promise<string>), ringId?: string): Promise<void> {
    if (this.value.phase === 'active') return;
    this.cleanup(); const generation = ++this.generation;
    this.pending = {}; this.hangingUp = false;
    const preparation = this.preparation = new AbortController();
    this.update({ ...idle(), phase: 'active', startedAt: Date.now() });
    try {
      // Audio starts in the click stack. With the microphone already granted, the daemon opens the provider
      // session meanwhile; otherwise it waits, so a denied microphone never makes a paid request.
      const preparing = this.deps.audio(frame => {
        if (generation !== this.generation || this.hangingUp || this.socket?.readyState !== 1) return;
        if (frame.byteLength % 2 || this.socket.bufferedAmount > VOICE_AUDIO_FRAME_MAX_BYTES * 4) {
          this.finish('disconnected'); return;
        }
        for (let offset = 0; offset < frame.byteLength; offset += VOICE_AUDIO_FRAME_MAX_BYTES)
          this.socket.send(frame.slice(offset, offset + VOICE_AUDIO_FRAME_MAX_BYTES));
      }, (itemId, ms, speaking) => {
        if (generation !== this.generation || this.hangingUp) return;
        this.send({ type: 'played', itemId, ms });
        if (this.value.speaking !== speaking) this.update({ speaking });
      }, () => { if (generation === this.generation && !this.hangingUp) this.finish('provider_error'); }, preparation.signal);
      const starting = this.deps.microphoneGranted().then(granted => granted ? undefined : preparing)
        .then(async () => {
          if (generation !== this.generation) throw new DOMException('Call cancelled', 'AbortError');
          const sessionId = typeof target === 'string' ? target : await target(preparation.signal);
          if (generation !== this.generation) throw new DOMException('Call cancelled', 'AbortError');
          return this.deps.start({ sessionId, ...(ringId ? { ringId } : {}) });
        });
      const [prepared, started] = await Promise.allSettled([preparing, starting]);
      if (generation !== this.generation || prepared.status === 'rejected' || started.status === 'rejected') {
        if (prepared.status === 'fulfilled') prepared.value.stop();
        if (generation === this.generation) this.finish(this.failure(prepared.status === 'rejected' ? prepared.reason : (started as PromiseRejectedResult).reason));
        if (started.status === 'fulfilled') this.abandon(started.value);
        return;
      }
      this.audio = prepared.value;
      const response = started.value;
      this.socket = this.deps.socket(response.callId, response.ticket);
      this.socket.binaryType = 'arraybuffer';
      this.timeout = setTimeout(() => this.finish('disconnected'), 15_000);
      this.socket.onopen = () => { if (this.hangingUp) this.send({ type: 'hangup' }); else clearTimeout(this.timeout); };
      this.socket.onmessage = event => {
        if (event.data instanceof ArrayBuffer) {
          if (event.data.byteLength > VOICE_AUDIO_FRAME_MAX_BYTES || event.data.byteLength % 2) { this.finish('provider_error'); return; }
          this.audio?.play(event.data); return;
        }
        try {
          if (typeof event.data !== 'string' || new TextEncoder().encode(event.data).byteLength > DAEMON_FRAME_MAX_BYTES) throw new Error('Invalid voice frame');
          const message = control.parse(JSON.parse(event.data));
          switch (message.type) {
            case 'submitted': break;
            case 'audio_started': this.audio?.beginItem(message.itemId); break;
            case 'state': if (!this.hangingUp) this.update({ state: message.state }); break;
            case 'ended': this.finish(message.reason, message.transcript); break;
            case 'transcript': {
              if (message.final !== undefined) {
                delete this.pending[message.role];
                this.update({ transcript: [...this.value.transcript, { role: message.role, text: message.final }] });
              } else {
                const text = (this.pending[message.role] ?? '') + message.delta;
                if (text.length > VOICE_CALL_RECORD_BOUNDS.maxEntryChars) throw new Error('Voice transcript entry too large');
                this.pending[message.role] = text;
              }
              const transcript = this.liveTranscript();
              if (transcript.length > VOICE_CALL_RECORD_BOUNDS.maxEntries
                || transcript.reduce((total, entry) => total + entry.text.length, 0) > VOICE_CALL_RECORD_BOUNDS.maxTranscriptChars)
                throw new Error('Voice transcript too large');
              break;
            }
          }
        } catch { this.finish('provider_error'); }
      };
      this.socket.onclose = () => this.finish('disconnected');
      this.socket.onerror = () => this.finish('disconnected');
    } catch (error) {
      if (generation === this.generation) this.finish(this.failure(error));
    }
  }
  private failure(error: unknown): NonNullable<VoiceCallSnapshot['reason']> {
    const denied = error instanceof DOMException && (error.name === 'NotAllowedError' || error.name === 'SecurityError');
    const code = refusal.safeParse(error instanceof ElowenApiError ? error.code : undefined);
    return denied ? 'mic_denied' : code.success ? code.data : 'provider_error';
  }
  /** A reservation this attempt no longer uses: consume its ticket and hang up so the daemon frees the slot. */
  private abandon(response: VoiceCallResponse) {
    consumeVoiceTicket(this.deps.socket, response.callId, response.ticket, { type: 'hangup' });
  }
  private liveTranscript = (): VoiceTranscriptEntry[] => [
    ...this.value.transcript,
    ...Object.entries(this.pending).map(([role, text]) => ({ role: role as VoiceTranscriptEntry['role'], text: text! })),
  ];
  hangup = () => {
    if (this.value.phase !== 'active' || this.hangingUp) return;
    if (!this.socket) { this.finish('hangup'); return; }
    this.hangingUp = true;
    this.audio?.stop(); this.audio = undefined;
    this.preparation?.abort();
    this.update({ state: 'ending', speaking: false });
    this.send({ type: 'hangup' });
    clearTimeout(this.timeout);
    this.timeout = setTimeout(() => this.finish('disconnected'), VOICE_CALL_STOP_BUDGET.browserWaitMs);
  };
  private finish(reason: NonNullable<VoiceCallSnapshot['reason']>, transcript = this.liveTranscript()) {
    if (this.value.phase !== 'active') return;
    ++this.generation; this.cleanup(); this.pending = {};
    this.update({ phase: (['mic_denied', 'disconnected'].includes(reason) || refusal.safeParse(reason).success) ? 'error' : 'ended', reason, transcript, speaking: false });
  }
  private cleanup() {
    clearTimeout(this.timeout); this.preparation?.abort(); this.preparation = undefined;
    this.audio?.stop(); this.audio = undefined;
    if (this.socket) {
      clearVoiceSocketHandlers(this.socket);
      this.socket.close(); this.socket = undefined;
    }
  }
  dismiss = () => { if (this.value.phase !== 'active') this.update(idle()); };
  dispose = () => { ++this.generation; this.cleanup(); };
}

export function useVoiceCall() {
  const ref = useRef<VoiceCallSession | null>(null);
  if (!ref.current) ref.current = new VoiceCallSession({
    audio: createVoiceAudio, start: elowenClient.startVoiceCall,
    // Browsers without a queryable microphone permission take the sequential path. `navigator.permissions`
    // itself is missing on older WebKit, where the call would throw before any promise exists; starting the
    // chain inside `then` turns that throw into the same rejection the unsupported `microphone` name gives.
    microphoneGranted: () => Promise.resolve()
      .then(() => navigator.permissions.query({ name: 'microphone' as PermissionName }))
      .then(status => status.state === 'granted', () => false),
    socket: createVoiceSocket,
  });
  const session = ref.current;
  const snapshot = useSyncExternalStore(session.subscribe, session.getSnapshot, session.getSnapshot);
  useEffect(() => () => session.dispose(), [session]);
  return { session, snapshot };
}
